/**
 * Regroup AI topics on the Insights page (S44 of documentation/pr-plans/insights-s0-plan-and-seats.md):
 * one Alpine store, `$store.siteTopicsRegroup`, that three parts under site-analytics/topic-editor read:
 * the button (regroup-button), the line under it (regroup-answer) and the panel (regroup-panel), so Sort
 * my searches can put the panel above its sections while the button sits in its header. The topic editor
 * (livewire/sites/topic-editor), which the page mounts once, seeds it as the page loads with when Regroup
 * may next run and feeds it TopicEditor's progress and result events.
 *
 * The store is the page's, not a site's: made once (app.js), it outlives a wire:navigate body swap, so
 * seed() starts it over for the page that loaded. Without that, a run the page before was following, for
 * another site or already ended, read "Regrouping…" on every page after it until a full load.
 *
 * The server decides when a run may start (SiteTopicRegroupWindow, D82): this only counts down to the
 * times it sends, and asks it again with every press.
 */
export const STORE = "siteTopicsRegroup";

export const STATE_IDLE = "idle";
export const STATE_RUNNING = "running";
export const STATE_WAITING = "waiting";
export const STATE_CAPPED = "capped";

const MINUTE_MS = 60000;

/** The bar never fills past this before the answer is saved. */
const MAX_SHARE = 0.95;

export function minutesUntil(at, now) {
    return Math.max(1, Math.ceil((at - now) / MINUTE_MS));
}

/**
 * How much of the bar is filled: toward the run's estimate by the time so far, never behind the step it
 * is on, and never to the end before the answer is saved.
 */
export function share(progress, now) {
    const seconds = Math.max(0, (now - Date.parse(progress.since)) / 1000);
    const byTime = seconds / Math.max(1, progress.estimate);
    const byStep = (progress.step - 1) / progress.steps;

    return Math.min(MAX_SHARE, Math.max(byStep, byTime));
}

/** Whole minutes left until the run's estimate, or null once it has passed it, when the panel claims none. */
export function minutesLeft(progress, now) {
    const left = Date.parse(progress.since) + progress.estimate * 1000 - now;

    return left <= 0 ? null : Math.max(1, Math.ceil(left / MINUTE_MS));
}

/** The button's words for the store's state, from the topic_editor lang keys regroup-button hands over. */
export function buttonLabel(store, strings) {
    switch (store.state) {
        case STATE_RUNNING:
            return store.isHidden ? strings.runningHidden.replace(":percent", String(store.percent)) : strings.running;
        case STATE_WAITING:
        case STATE_CAPPED:
            return strings.again.replace(":minutes", String(store.waitMinutes));
        default:
            return strings.idle;
    }
}

const toTime = (iso) => (iso ? Date.parse(iso) : null);

export default function RegroupStore() {
    return {
        /** TopicEditor::REGROUP_PROGRESS_EVENT's detail while a run is open, else null. */
        progress: null,
        isHidden: false,
        /** When an unchanged preference may run, in ms, or null for now. */
        nextAllowedAt: null,
        /** When the hour's Regroup runs free up, in ms, or null while they are not all used. */
        capFreesAt: null,
        now: Date.now(),
        ticker: null,

        /**
         * The page's load, with SiteTopicRegroupWindow::toEvent() as it loaded. Starts over first: whatever
         * run the page before was following is not this page's, and the run open here, if any, is read back
         * by this page's editor (TopicEditor::checkRegroup()).
         */
        seed(opening) {
            this.progress = null;
            this.isHidden = false;
            this.allow(opening);
        },

        /** SiteTopicRegroupWindow::toEvent(), as a load, a press or a run's end left it. */
        allow(opening) {
            this.nextAllowedAt = toTime(opening?.nextAllowedAt);
            this.capFreesAt = toTime(opening?.capFreesAt);
            this.tick();
        },

        follow(detail) {
            this.progress = detail;
            this.tick();
        },

        answer(detail) {
            if (detail.window) {
                this.allow(detail.window);
            }

            if (detail.ended === true) {
                this.progress = null;
                this.isHidden = false;
            }

            this.tick();
        },

        /** Counts every second while there is something to count, and stops when there is not. */
        tick() {
            this.now = Date.now();
            const isCounting = this.progress !== null || (this.nextAllowedAt !== null && this.nextAllowedAt > this.now);

            if (isCounting && this.ticker === null) {
                this.ticker = setInterval(() => this.tick(), 1000);
            } else if (!isCounting && this.ticker !== null) {
                clearInterval(this.ticker);
                this.ticker = null;
            }
        },

        get state() {
            if (this.progress !== null) {
                return STATE_RUNNING;
            }

            if (this.capFreesAt !== null && this.capFreesAt > this.now) {
                return STATE_CAPPED;
            }

            return this.nextAllowedAt !== null && this.nextAllowedAt > this.now ? STATE_WAITING : STATE_IDLE;
        },

        get isInactive() {
            return this.state === STATE_CAPPED;
        },

        get percent() {
            return this.progress === null ? 0 : Math.round(share(this.progress, this.now) * 100);
        },

        get minutesLeft() {
            return this.progress === null ? null : minutesLeft(this.progress, this.now);
        },

        /** The minutes "Regroup again in N min" says: to when the cap frees while capped, else to the hour's end. */
        get waitMinutes() {
            if (this.state === STATE_CAPPED) {
                return minutesUntil(this.capFreesAt, this.now);
            }

            return this.state === STATE_WAITING ? minutesUntil(this.nextAllowedAt, this.now) : null;
        },

        get isPanelShown() {
            return this.progress !== null && !this.isHidden;
        },

        /**
         * What a press of the button does: while a run is open it shows the panel ("panel") and never
         * opens the dialog; once the hour's Regroup runs are used it does nothing (null); otherwise it
         * opens the dialog ("dialog"), which shows any wait itself.
         */
        press() {
            switch (this.state) {
                case STATE_RUNNING:
                    this.isHidden = false;

                    return "panel";
                case STATE_CAPPED:
                    return null;
                default:
                    return "dialog";
            }
        },

        hide() {
            this.isHidden = true;
        },

        /** The button's words now (buttonLabel()). */
        label(strings) {
            return buttonLabel(this, strings);
        },
    };
}
